import type { StateIconProps } from '@spotify/confidence-core-react';
import {
  ObservationsV1IssueCloseReason,
  ObservationsV1IssueState,
} from '@spotify/confidence-graphql';

export const IssueState = {
  Waiting: 'WAITING',
  Working: 'WORKING',
  Closed: ObservationsV1IssueState.Closed,
  Open: ObservationsV1IssueState.Open,
  InProgress: ObservationsV1IssueState.InProgress,
} as const;

export type IssueState = (typeof IssueState)[keyof typeof IssueState];

export type CanonicalIssueState =
  | typeof IssueState.Waiting
  | typeof IssueState.Working
  | typeof IssueState.Closed;

const NORMALIZED_ISSUE_STATES: Record<IssueState, CanonicalIssueState> = {
  [IssueState.Waiting]: IssueState.Waiting,
  [IssueState.Open]: IssueState.Waiting,
  [IssueState.Working]: IssueState.Working,
  [IssueState.InProgress]: IssueState.Working,
  [IssueState.Closed]: IssueState.Closed,
};

const ISSUE_STATE_LABELS: Record<CanonicalIssueState, string> = {
  [IssueState.Waiting]: 'Waiting',
  [IssueState.Working]: 'Working',
  [IssueState.Closed]: 'Closed',
};

const ISSUE_STATE_ICON_STATES: Record<
  CanonicalIssueState,
  StateIconProps['state']
> = {
  [IssueState.Waiting]: 'draft',
  [IssueState.Working]: 'live',
  [IssueState.Closed]: 'ended',
};

export const isIssueState = (value: unknown): value is IssueState =>
  typeof value === 'string' &&
  Object.values(IssueState).some(state => state === value);

export const normalizeIssueState = (state: IssueState): CanonicalIssueState =>
  NORMALIZED_ISSUE_STATES[state];

export const normalizeIssueStates = (
  states: IssueState[],
): CanonicalIssueState[] => [
  ...new Set(states.map(state => normalizeIssueState(state))),
];

export const getIssueStateLabel = (state: IssueState): string =>
  ISSUE_STATE_LABELS[normalizeIssueState(state)];

export const getIssueCloseReasonLabel = (
  closeReason: ObservationsV1IssueCloseReason | null | undefined,
): string => {
  switch (closeReason) {
    case ObservationsV1IssueCloseReason.Fixed:
      return 'Fixed';
    case ObservationsV1IssueCloseReason.WontFix:
      return "Won't fix";
    case ObservationsV1IssueCloseReason.NonIssue:
      return 'Not an issue';
    default:
      return 'Closed';
  }
};

export const getIssueStateIconState = (
  state: IssueState,
): StateIconProps['state'] =>
  ISSUE_STATE_ICON_STATES[normalizeIssueState(state)];
